/**
 * Form building blocks and validation.
 *
 *   field('email', 'Email', { type: 'email', name: 'email' })       one labelled control with an error slot
 *   selectField / radioCards / tagInput / combo                       richer controls
 *   validate('#form')                                                  marks .field.bad and focuses the first problem
 *   readForm('#form')                                                  { name: value } for every control with a name
 *   showFieldErrors('#form', apiError.fieldErrors)                     puts server messages next to the controls
 *
 * Controls are identified by `name`, which matches the API's field names, so server-side validation
 * errors land on the right input.
 */
import { esc, html, raw } from '../core/html.js';
import { SKILL_SUGGESTIONS } from '../core/constants.js';
import { $, $$ } from '../core/dom.js';
import { register } from '../core/events.js';
import { ic } from './components.js';

/* ---------- plain fields ---------- */

export function field(id, label, o = {}) {
  const required = o.req !== false;
  const type = o.type || 'text';
  const name = o.name || id;
  let control;
  if (o.opts) control = combo(id, o.opts, o.val, o.ph, { name });
  else if (o.rows) {
    control = html`<textarea id="${id}" name="${name}" rows="${o.rows}" placeholder="${o.ph || ''}" maxlength="${o.max || 2000}">${o.val ?? ''}</textarea>`;
  } else {
    const attrs = [
      type === 'email' ? 'autocomplete="email" inputmode="email"' : '',
      type === 'tel' ? 'autocomplete="tel" inputmode="tel"' : '',
      type === 'password' ? `autocomplete="${o.autocomplete || 'current-password'}"` : '',
      o.autocomplete && type !== 'password' ? `autocomplete="${esc(o.autocomplete)}"` : '',
      o.min != null ? `min="${esc(o.min)}"` : '',
      o.max != null ? `max="${esc(o.max)}"` : '',
      o.maxlength ? `maxlength="${esc(o.maxlength)}"` : '',
      o.inputmode ? `inputmode="${esc(o.inputmode)}"` : '',
      o.pattern ? `data-pattern="${esc(o.pattern)}"` : '',
      o.minlen ? `data-minlen="${esc(o.minlen)}"` : '',
      o.disabled ? 'disabled' : '',
      o.act ? `data-input="${esc(o.act)}"` : '',
    ].filter(Boolean).join(' ');
    control = html`<input id="${id}" name="${name}" type="${type}" value="${o.val ?? ''}" placeholder="${o.ph || ''}" ${raw(attrs)}>`;
  }
  const defaultError = type === 'email' ? 'Enter a valid email, like name@example.com.' : 'This field is required.';
  return html`<div class="field ${o.cls || ''}" data-req="${required}" data-type="${type}"><label for="${id}">${label}${required ? '' : html` <span class="mu">(optional)</span>`}</label>${control}${o.help ? html`<span class="help">${o.help}</span>` : ''}<span class="err" role="alert">${o.err || defaultError}</span></div>`;
}

/** A native <select> for value/label pairs, e.g. batches by id. */
export function selectField(id, label, options, o = {}) {
  const name = o.name || id;
  const required = o.req !== false;
  return html`<div class="field ${o.cls || ''}" data-req="${required}" data-type="select"><label for="${id}">${label}${required ? '' : html` <span class="mu">(optional)</span>`}</label>
    <select id="${id}" name="${name}" ${o.act ? raw(`data-change="${esc(o.act)}"`) : ''}>${o.placeholder ? html`<option value="">${o.placeholder}</option>` : ''}${options.map(([value, text]) => html`<option value="${value}" ${String(value) === String(o.val ?? '') ? raw('selected') : ''}>${text}</option>`)}</select>
    ${o.help ? html`<span class="help">${o.help}</span>` : ''}<span class="err" role="alert">${o.err || 'Choose an option.'}</span></div>`;
}

/* ---------- searchable dropdown ---------- */

export function combo(id, opts, value, placeholder, { name = id, change = '' } = {}) {
  const current = value ?? opts[0] ?? '';
  return html`<div class="combo"><input id="${id}" name="${name}" class="combo-in" role="combobox" aria-expanded="false" aria-autocomplete="list" aria-controls="${id}-lb" autocomplete="off" spellcheck="false" value="${current}" data-last="${current}" data-opts="${JSON.stringify(opts)}" placeholder="${placeholder || 'Type to search'}" ${change ? raw(`data-change="${esc(change)}"`) : ''}>${ic('chev', 'chev')}<ul class="combo-list" id="${id}-lb" role="listbox" hidden></ul></div>`;
}

function comboRender(input, query) {
  const list = input.parentElement.querySelector('.combo-list');
  const opts = JSON.parse(input.dataset.opts);
  const q = (query || '').toLowerCase();
  const found = q ? opts.filter((o) => o.toLowerCase().includes(q)) : opts;
  input._found = found;
  input._index = q ? 0 : Math.max(0, found.indexOf(input.dataset.last));
  const mark = (o) => {
    if (!q) return html`${o}`;
    const i = o.toLowerCase().indexOf(q);
    return html`${o.slice(0, i)}<mark>${o.slice(i, i + q.length)}</mark>${o.slice(i + q.length)}`;
  };
  list.innerHTML = found.length
    ? found.map((o, i) => html`<li role="option" id="${input.id}-o${i}" aria-selected="${o === input.dataset.last}" class="${i === input._index ? 'act' : ''}" data-v="${o}"><span>${mark(o)}</span>${o === input.dataset.last ? ic('check') : ''}</li>`).join('')
    : '<li class="none">No matches. Try another word.</li>';
  list.hidden = false;
  input.setAttribute('aria-expanded', 'true');
  comboActivate(input);
}

function comboActivate(input) {
  const list = input.parentElement.querySelector('.combo-list');
  list.querySelectorAll('li[data-v]').forEach((li, i) => li.classList.toggle('act', i === input._index));
  const active = list.querySelector('li.act');
  if (active) {
    input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView?.({ block: 'nearest' });
  }
}

function comboClose(input) {
  const list = input.parentElement.querySelector('.combo-list');
  if (list) list.hidden = true;
  input.setAttribute('aria-expanded', 'false');
}

function comboPick(input, value) {
  input.value = value;
  const changed = input.dataset.last !== value;
  input.dataset.last = value;
  comboClose(input);
  if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
}

const isCombo = (el) => el?.classList?.contains('combo-in');
const isTagInput = (el) => el?.classList?.contains('tagin-in');

/* ---------- skill tags ---------- */

export function tagInput(id, preset = [], { name = id, suggestions } = {}) {
  return html`<div class="tagin" id="${id}" data-morph="skip" data-name="${name}" data-tags="${JSON.stringify(preset)}" ${suggestions ? raw(`data-suggest="${esc(JSON.stringify(suggestions))}"`) : ''}><span class="tags">${tagChips(preset, id)}</span><input class="tagin-in" id="${id}-in" placeholder="${preset.length ? 'Add another skill' : 'Type a skill, press Enter'}" autocomplete="off" aria-label="Add skill" role="combobox" aria-expanded="false"><ul class="combo-list" hidden role="listbox"></ul></div>`;
}

const tagChips = (items, id) => items.map((t, i) => html`<span class="tchip">${t}<button type="button" aria-label="Remove ${t}" data-act="tags.remove" data-tag-id="${id}" data-index="${i}">${ic('x')}</button></span>`);

export function getTags(id) {
  const el = document.getElementById(id);
  if (!el) return [];
  try {
    return JSON.parse(el.dataset.tags);
  } catch {
    return [];
  }
}

function setTags(id, list) {
  const el = document.getElementById(id);
  el.dataset.tags = JSON.stringify(list);
  el.querySelector('.tags').innerHTML = tagChips(list, id).map(String).join('');
  el.querySelector('input').placeholder = list.length ? 'Add another skill' : 'Type a skill, press Enter';
  const f = el.closest('.field');
  if (f && list.length) f.classList.remove('bad');
}

function suggestionPool(box) {
  try {
    return box.dataset.suggest ? JSON.parse(box.dataset.suggest) : SKILL_SUGGESTIONS;
  } catch {
    return SKILL_SUGGESTIONS;
  }
}

export function addTag(id, value) {
  const text = value.trim().replace(/,$/, '').slice(0, 40);
  if (!text) return;
  const box = document.getElementById(id);
  const canonical = suggestionPool(box).find((s) => s.toLowerCase() === text.toLowerCase()) || text;
  const list = getTags(id);
  if (list.length < 30 && !list.some((x) => x.toLowerCase() === canonical.toLowerCase())) list.push(canonical);
  setTags(id, list);
  const input = box.querySelector('input');
  input.value = '';
  tagSuggest(input);
}

export function removeTag(id, index) {
  const list = getTags(id);
  list.splice(index, 1);
  setTags(id, list);
}

function tagSuggest(input) {
  const box = input.closest('.tagin');
  const list = box.querySelector('.combo-list');
  const q = input.value.trim().toLowerCase();
  const have = getTags(box.id).map((x) => x.toLowerCase());
  const found = suggestionPool(box).filter((s) => !have.includes(s.toLowerCase()) && (!q || s.toLowerCase().includes(q))).slice(0, 8);
  input._found = found;
  input._index = 0;
  if (!found.length && !q) {
    list.hidden = true;
    return;
  }
  const typed = input.value.trim();
  const addTyped = q && !found.some((s) => s.toLowerCase() === q);
  list.innerHTML = found.map((s, i) => html`<li data-t="${s}" class="${i === 0 ? 'act' : ''}">${s}</li>`).join('')
    + (addTyped ? html`<li data-t="${typed}" class="${found.length ? '' : 'act'}">Add “${typed}”</li>`.toString() : '');
  list.hidden = false;
  input.setAttribute('aria-expanded', 'true');
}

/* ---------- radio cards ---------- */

export function radioCards(name, options, current) {
  return html`<div class="rcards" role="radiogroup">${options.map((o) => {
    const [value, desc] = Array.isArray(o) ? o : [o, ''];
    return html`<label class="rcard"><input type="radio" name="${name}" value="${value}" ${value === current ? raw('checked') : ''}><span><b>${value}</b>${desc ? html`<small>${desc}</small>` : ''}</span></label>`;
  })}</div>`;
}

export function radioField(name, label, options, current, err = 'Choose one.') {
  return html`<div class="field" data-req="radio" data-name="${name}"><label>${label}</label>${radioCards(name, options, current)}<span class="err" role="alert">${err}</span></div>`;
}

export function tagField(id, label, { name = id, preset = [], help = 'Add 2 to 6 skills. Start typing to see suggestions.', err = 'Add at least one skill.', suggestions, req = true } = {}) {
  return html`<div class="field" data-req="${req ? 'tags' : 'tags-optional'}"><label for="${id}-in">${label}</label>${tagInput(id, preset, { name, suggestions })}<span class="help">${help}</span><span class="err" role="alert">${err}</span></div>`;
}

export function inrField(id, label, o = {}) {
  const name = o.name || id;
  return html`<div class="field" data-req="${o.req !== false}" data-type="${o.number ? 'number' : 'text'}"><label for="${id}">${label}</label><div class="ingroup"><span>₹</span><input id="${id}" name="${name}" ${o.number ? raw('type="number" min="0" step="1" inputmode="numeric"') : raw('type="text"')} value="${o.val ?? ''}" placeholder="${o.ph || ''}"></div>${o.help ? html`<span class="help">${o.help}</span>` : ''}<span class="err" role="alert">${o.err || 'Enter an amount.'}</span></div>`;
}

/* ---------- validation and reading ---------- */

const EMAIL = /^[^@\s]{1,64}@[^@\s]+\.[^@\s]{2,}$/;

export function validate(scope) {
  const root = typeof scope === 'string' ? $(scope) : scope;
  if (!root) return false;
  let ok = true;
  let first = null;
  for (const f of $$('.field', root)) {
    const kind = f.dataset.req;
    if (kind === 'tags-optional') continue;
    if (kind === 'tags' || kind === 'radio') {
      const bad = kind === 'tags' ? getTags($('.tagin', f).id).length === 0 : !f.querySelector('input:checked');
      f.classList.toggle('bad', bad);
      if (bad) {
        ok = false;
        first = first || f.querySelector('input');
      }
      continue;
    }
    const input = f.querySelector('input:not([type=checkbox]):not([type=radio]), select, textarea');
    if (!input) continue;
    const v = input.value.trim();
    let bad = f.dataset.req === 'true' && !v;
    if (!bad && v && f.dataset.type === 'email' && !EMAIL.test(v)) bad = true;
    if (!bad && v && f.dataset.type === 'number') {
      const n = Number(v);
      if (Number.isNaN(n) || (input.min !== '' && n < Number(input.min)) || (input.max !== '' && n > Number(input.max))) bad = true;
    }
    if (!bad && v && input.dataset.minlen && v.length < Number(input.dataset.minlen)) bad = true;
    if (!bad && v && input.dataset.pattern && !new RegExp(input.dataset.pattern).test(v)) bad = true;
    f.classList.toggle('bad', bad);
    if (bad) {
      ok = false;
      first = first || input;
    }
  }
  first?.focus();
  return ok;
}

/** Values of every control with a `name`: text -> string, checkbox -> boolean, tags -> array, radios -> chosen value. */
export function readForm(scope) {
  const root = typeof scope === 'string' ? $(scope) : scope;
  const out = {};
  if (!root) return out;
  for (const el of $$('input[name], select[name], textarea[name]', root)) {
    if (el.type === 'radio') {
      if (el.checked) out[el.name] = el.value;
      else if (!(el.name in out)) out[el.name] = '';
    } else if (el.type === 'checkbox') out[el.name] = el.checked;
    else out[el.name] = el.value.trim();
  }
  for (const box of $$('.tagin[data-name]', root)) out[box.dataset.name] = getTags(box.id);
  return out;
}

export function showFieldErrors(scope, errors) {
  const root = typeof scope === 'string' ? $(scope) : scope;
  if (!root) return false;
  let any = false;
  for (const [name, message] of Object.entries(errors || {})) {
    const key = name.split('.')[0];
    const control = root.querySelector(`[name="${CSS.escape(key)}"], .tagin[data-name="${CSS.escape(key)}"]`);
    const f = control?.closest('.field');
    if (!f) continue;
    f.classList.add('bad');
    const err = f.querySelector('.err');
    if (err) err.textContent = message;
    if (!any) {
      (control.matches('input, select, textarea') ? control : control.querySelector('input'))?.focus();
      any = true;
    }
  }
  return any;
}

export function clearFieldErrors(scope) {
  const root = typeof scope === 'string' ? $(scope) : scope;
  for (const f of $$('.field.bad', root)) f.classList.remove('bad');
}

/* ---------- document-level behaviour (installed once) ---------- */

let installed = false;

export function installForms() {
  if (installed) return;
  installed = true;
  register({ 'tags.remove': (el) => removeTag(el.dataset.tagId, Number(el.dataset.index)) });
  document.addEventListener('focusin', (e) => {
    if (isCombo(e.target)) {
      comboRender(e.target, '');
      try {
        e.target.select();
      } catch { /* not selectable */ }
    } else if (isTagInput(e.target)) tagSuggest(e.target);
  });
  document.addEventListener('input', (e) => {
    if (isCombo(e.target)) comboRender(e.target, e.target.value);
    else if (isTagInput(e.target)) tagSuggest(e.target);
  });
  document.addEventListener('keydown', (e) => {
    const t = e.target;
    if (isCombo(t)) {
      const open = t.getAttribute('aria-expanded') === 'true';
      if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        e.preventDefault();
        comboRender(t, '');
        return;
      }
      if (!open) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = (t._found || []).length;
        if (!n) return;
        t._index = (t._index + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
        comboActivate(t);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const v = (t._found || [])[t._index];
        if (v != null) comboPick(t, v);
      } else if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        t.value = t.dataset.last;
        comboClose(t);
      } else if (e.key === 'Tab') {
        const v = (t._found || [])[t._index];
        if (v != null && t.value !== t.dataset.last) comboPick(t, v);
      }
    } else if (isTagInput(t)) {
      const box = t.closest('.tagin');
      const list = box.querySelector('.combo-list');
      const items = [...list.querySelectorAll('li[data-t]')];
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const active = items.find((l) => l.classList.contains('act'));
        addTag(box.id, active && !list.hidden ? active.dataset.t : t.value);
      } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && items.length) {
        e.preventDefault();
        let i = items.findIndex((l) => l.classList.contains('act'));
        i = (i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach((l, j) => l.classList.toggle('act', j === i));
      } else if (e.key === 'Backspace' && !t.value) {
        const current = getTags(box.id);
        if (current.length) {
          current.pop();
          setTags(box.id, current);
        }
      } else if (e.key === 'Escape' && !list.hidden) {
        e.stopImmediatePropagation();
        list.hidden = true;
      }
    }
  }, true);
  document.addEventListener('mousedown', (e) => {
    const option = e.target.closest?.('.combo-list li[data-v]');
    if (option) {
      e.preventDefault();
      comboPick(option.closest('.combo').querySelector('.combo-in'), option.dataset.v);
      return;
    }
    const suggestion = e.target.closest?.('.tagin li[data-t]');
    if (suggestion) {
      e.preventDefault();
      addTag(suggestion.closest('.tagin').id, suggestion.dataset.t);
    }
  });
  document.addEventListener('focusout', (e) => {
    const t = e.target;
    if (isCombo(t)) {
      const opts = JSON.parse(t.dataset.opts);
      const match = opts.find((o) => o.toLowerCase() === t.value.trim().toLowerCase());
      if (match) comboPick(t, match);
      else {
        t.value = t.dataset.last;
        comboClose(t);
      }
    } else if (isTagInput(t)) {
      const list = t.closest('.tagin').querySelector('.combo-list');
      list.hidden = true;
      t.setAttribute('aria-expanded', 'false');
    }
  });
  document.addEventListener('click', (e) => {
    const box = e.target.closest?.('.tagin');
    if (box && (e.target === box || e.target.classList.contains('tags'))) box.querySelector('input').focus();
  });
}
